import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { 
  ArrowLeft, 
  RefreshCw, 
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Package,
  UserCheck,
  XCircle
} from 'lucide-react';
import { ApprovalInterface } from '../components/jobs/ApprovalInterface';
import { Job, jobService, BatchStatus } from '../services/job';
import { useToast } from '../hooks/use-toast';
import { useOrganization } from '../contexts/OrganizationContext';

export const ApprovalPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { hasPermission } = useOrganization();

  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [batchStatus, setBatchStatus] = useState<BatchStatus | null>(null);
  
  // Simple progress tracking
  const [approvedInSession, setApprovedInSession] = useState<number>(0);
  const [rejectedInSession, setRejectedInSession] = useState<number>(0);
  const [checkingForMore, setCheckingForMore] = useState(false);

  // Get query parameters
  const batchId = searchParams.get('batch_id');
  const storeId = searchParams.get('store_id');
  const limit = 100;

  const fetchBatchStatus = async () => {
    if (!batchId) {
      console.log('No batch_id in URL, skipping batch status fetch');
      return;
    }
    
    try {
      console.log('Fetching batch status for:', batchId);
      const response = await jobService.getBatchStatus(batchId);
      setBatchStatus(response.data);
      console.log('Batch status loaded:', response.data);
    } catch (error) {
      console.error('Failed to fetch batch status:', error);
    }
  };

  // Function to update both batch status and notify parent dashboard
  const updateStats = async () => {
    await fetchBatchStatus();
    
    // Trigger a custom event to update the main dashboard stats
    window.dispatchEvent(new CustomEvent('jobStatsUpdate'));
  };

  const fetchJobs = async (showLoading = true) => {
    if (showLoading) setLoading(true);
    setError(null);

    try {
      const params: any = {
        limit,
        offset: 0,
        status: 'processed_pending_approval'
      };

      if (batchId) params.batch_id = batchId;
      if (storeId) params.store_id = storeId;

      const response = await jobService.getJobs(params);
      
      if (response.success) {
        const newJobs = response.data.jobs;
        setJobs(newJobs);
        
        // If we found new jobs after showing empty state, show a notification
        if (newJobs.length > 0 && jobs.length === 0 && totalProcessedInSession > 0) {
          toast({
            title: 'New Jobs Available',
            description: `${newJobs.length} new images are ready for approval!`,
            variant: 'default',
          });
        }
      } else {
        throw new Error('Failed to fetch jobs');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch jobs pending approval';
      setError(errorMessage);
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    // Reset session counters on manual refresh
    setApprovedInSession(0);
    setRejectedInSession(0);
    Promise.all([
      fetchJobs(false),
      fetchBatchStatus()
    ]).finally(() => {
      setRefreshing(false);
    });
  };

  const handleApprove = async (jobId: string) => {
    try {
      const response = await jobService.approveJob(jobId);

      if (response.success) {
        // Remove the approved job from the list
        setJobs(prev => {
          const newJobs = prev.filter(job => job.id !== jobId);

          // If this was the last job on the current page and we have a batch_id,
          // automatically load more jobs from the batch
          if (newJobs.length === 0 && batchId) {
            setTimeout(() => {
              fetchJobs(false); // Load next batch of jobs without showing loading
            }, 500);
          }

          return newJobs;
        });

        // Update session counters
        setApprovedInSession(prev => prev + 1);

        // Update batch progress and notify dashboard
        await updateStats();

        toast({
          title: 'Job Approved',
          description: 'The image has been approved and will be uploaded to Magento.',
          variant: 'default',
        });
      } else {
        throw new Error(response.message || 'Failed to approve job');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to approve job';
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    }
  };

  const handleReject = async (jobId: string, reason?: string) => {
    try {
      const response = await jobService.rejectJob(jobId, reason);
      
      if (response.success) {
        // Remove the rejected job from the list
        setJobs(prev => {
          const newJobs = prev.filter(job => job.id !== jobId);
          
          // If this was the last job on the current page and we have a batch_id, 
          // automatically load more jobs from the batch
          if (newJobs.length === 0 && batchId) {
            setTimeout(() => {
              fetchJobs(false); // Load next batch of jobs without showing loading
            }, 500);
          }
          
          return newJobs;
        });
        
        // Update session counters
        setRejectedInSession(prev => prev + 1);
        
        // Update batch progress and notify dashboard
        await updateStats(); 
        
        toast({
          title: 'Job Rejected',
          description: 'The image has been rejected and will not be processed further.',
          variant: 'default',
        });
      } else {
        throw new Error(response.message || 'Failed to reject job');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to reject job';
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    }
  };

  const handleBulkApprove = async (jobIds: string[]) => {
    try {
      const response = await jobService.bulkApproveJobs(jobIds);

      if (response.success) {
        // Remove approved jobs from the list
        setJobs(prev => {
          const newJobs = prev.filter(job => !jobIds.includes(job.id));

          // If we cleared all jobs on the current page and we have a batch_id,
          // automatically load more jobs from the batch
          if (newJobs.length === 0 && batchId) {
            setTimeout(() => {
              fetchJobs(false); // Load next batch of jobs without showing loading
            }, 500);
          }

          return newJobs;
        });

        // Update session counters
        setApprovedInSession(prev => prev + jobIds.length);

        // Update batch progress and notify dashboard
        await updateStats();

        toast({
          title: 'Bulk Approval Successful',
          description: `${response.data.approved_count} images have been approved and will be uploaded to Magento.`,
          variant: 'default',
        });
      } else {
        throw new Error(response.message || 'Failed to approve jobs');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to approve jobs';
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    }
  };

  const handleBackToJobs = () => {
    if (batchId) {
      navigate(`/jobs?batch_id=${batchId}`);
    } else {
      navigate('/jobs');
    }
  };

  // Calculate session progress
  const totalProcessedInSession = approvedInSession + rejectedInSession;

  useEffect(() => {
    fetchJobs();
    fetchBatchStatus();
  }, [batchId, storeId]);

  // Auto-refresh when all jobs are completed to check for new jobs
  useEffect(() => {
    // Only auto-refresh if we're showing the empty state and have processed some jobs
    if (!loading && !error && jobs.length === 0 && totalProcessedInSession > 0 && !checkingForMore) {
      // Wait a bit before checking for new jobs
      const timer = setTimeout(async () => {
        setCheckingForMore(true);
        await fetchJobs(false); // Refresh without showing loading state
        setCheckingForMore(false);
      }, 2000); // Wait 2 seconds before checking

      return () => clearTimeout(timer);
    }
  }, [jobs.length, loading, error, totalProcessedInSession, checkingForMore]);

  const pageTitle = batchId ? 'Batch Approval' : 'Image Approval';
  const pageDescription = batchId 
    ? 'Review and approve processed images from this batch'
    : 'Review and approve all processed images';

  return (
    <div className="container mx-auto px-4 py-6 space-y-6">
      {/* Back Button */}
      <div>
        <Button
          variant="outline"
          onClick={handleBackToJobs}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Jobs
        </Button>
      </div>

      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{pageTitle}</h1>
          <p className="text-gray-600">{pageDescription}</p>
        </div>
        <div className="flex items-center gap-3">
          {batchId && (
            <Badge variant="outline" className="px-3 py-1">
              Batch: {batchId.slice(0, 8)}...
            </Badge>
          )}
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Simple Session Progress - Only show when there are jobs to review */}
      {totalProcessedInSession > 0 && jobs.length > 0 && (
        <Alert className="border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950/20">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800 dark:text-green-200">
            <strong>Session Progress:</strong> You've processed {totalProcessedInSession} images 
            ({approvedInSession} approved, {rejectedInSession} rejected)
          </AlertDescription>
        </Alert>
      )}

      {/* Batch Status - Same Style as Dashboard */}
      {batchStatus && (
        <Card className="border-2 border-border bg-gradient-to-r from-muted/20 to-muted/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl">
              <Package className="h-5 w-5 text-muted-foreground" />
              Batch Operations Monitor
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Total Jobs */}
              <Card className="border-2 bg-muted/50 border-border">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="p-2 rounded-lg bg-muted/70">
                      <Package className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <span className="text-2xl font-bold text-muted-foreground">
                      {batchStatus.total_jobs || 0}
                    </span>
                  </div>
                  <p className="text-sm font-medium text-foreground">Total Jobs</p>
                </CardContent>
              </Card>

              {/* Business Critical Statuses */}
              {[
                { key: 'processed_pending_approval', label: 'Pending Approval', color: 'orange', Icon: UserCheck },
                { key: 'uploading_to_magento', label: 'Uploading', color: 'purple', Icon: TrendingUp },
                { key: 'complete', label: 'Complete', color: 'green', Icon: CheckCircle },
                { key: 'failed', label: 'Failed', color: 'red', Icon: XCircle },
                { key: 'rejected', label: 'Rejected', color: 'gray', Icon: XCircle }
              ].map(({ key, label, color, Icon }) => {
                const count = batchStatus.status_breakdown[key as keyof typeof batchStatus.status_breakdown] || 0;
                const colorClasses = {
                  orange: { 
                    bg: 'bg-card border-orange-200 dark:border-orange-800', 
                    iconBg: 'bg-orange-100 dark:bg-orange-950/50', 
                    text: 'text-orange-600 dark:text-orange-400' 
                  },
                  purple: { 
                    bg: 'bg-card border-purple-200 dark:border-purple-800', 
                    iconBg: 'bg-purple-100 dark:bg-purple-950/50', 
                    text: 'text-purple-600 dark:text-purple-400' 
                  },
                  green: { 
                    bg: 'bg-card border-green-200 dark:border-green-800', 
                    iconBg: 'bg-green-100 dark:bg-green-950/50', 
                    text: 'text-green-600 dark:text-green-400' 
                  },
                  red: { 
                    bg: 'bg-card border-red-200 dark:border-red-800', 
                    iconBg: 'bg-red-100 dark:bg-red-950/50', 
                    text: 'text-red-600 dark:text-red-400' 
                  },
                  gray: { 
                    bg: 'bg-card border-gray-200 dark:border-gray-800', 
                    iconBg: 'bg-gray-100 dark:bg-gray-950/50', 
                    text: 'text-gray-600 dark:text-gray-400' 
                  }
                };
                const classes = colorClasses[color as keyof typeof colorClasses];

                return (
                  <Card key={key} className={`border-2 ${classes.bg}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className={`p-2 rounded-lg ${classes.iconBg}`}>
                          <Icon className={`h-5 w-5 ${classes.text}`} />
                        </div>
                        <span className={`text-2xl font-bold ${classes.text}`}>
                          {count}
                        </span>
                      </div>
                      <p className="text-sm font-medium text-foreground">{label}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Error State */}
      {error && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {error}
          </AlertDescription>
        </Alert>
      )}

      {/* Loading State */}
      {loading && (
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <div className="text-center">
              <RefreshCw className="h-8 w-8 animate-spin text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">Loading jobs pending approval...</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Approval Interface */}
      {!loading && !error && (
        <ApprovalInterface
          jobs={jobs}
          onApprove={handleApprove}
          onReject={handleReject}
          onBulkApprove={handleBulkApprove}
          loading={refreshing}
          canApprove={hasPermission('jobs', 'approve')}
          canReject={hasPermission('jobs', 'reject')}
        />
      )}

      {/* Empty State */}
      {!loading && !error && jobs.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            {checkingForMore ? (
              <>
                <RefreshCw className="h-12 w-12 animate-spin text-blue-500 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Checking for more jobs...
                </h3>
                <p className="text-gray-500 text-center">
                  Looking for additional images that need approval
                </p>
              </>
            ) : (
              <>
                <CheckCircle className="h-12 w-12 text-green-500 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {totalProcessedInSession > 0 ? '🎉 Session Complete!' : 'All caught up!'}
                </h3>
                <p className="text-gray-500 text-center mb-6">
                  {totalProcessedInSession > 0 
                    ? `You've successfully reviewed ${totalProcessedInSession} images in this session!`
                    : batchId 
                      ? 'All images in this batch have been reviewed.'
                      : 'No images are currently pending approval.'
                  }
                </p>
                <div className="flex gap-3">
                  <Button onClick={handleBackToJobs}>
                    View All Jobs
                  </Button>
                  <Button variant="outline" onClick={() => navigate('/products')}>
                    Process More Images
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ApprovalPage;